import { Localized } from "@fluent/react/compat";
import React, { FunctionComponent } from "react";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import { TimeSeriesUserProfilesJSON } from "coral-common/common/lib/types/dashboard";
import { useImmediateFetch } from "coral-framework/lib/relay/fetch";

import { DashboardBox, DashboardComponentHeading } from "../components";
import createDashboardFetch from "../createDashboardFetch";

import { useMemo } from "react";
import colors from "coral-ui/theme/colors";
import { LineChart, Line } from "recharts";
import { CHART_COLOR_GREY_200 } from "coral-admin/routes/Dashboard/sections/ChartColors";
import { CartesianGrid } from "recharts";
import useDateTimeFormatter from "coral-framework/hooks/useDateTimeFormatter";
import { Tooltip } from "recharts";

import styles from "./OIDCActivity.css"
import { useEffect } from "react";
import { setTimeout } from "timers";
import { Flex, Spinner } from "coral-ui/components/v2";
import { OIDCActivityPie } from "./OIDCActivityPie";

interface Props {
  siteID: string;
  lastUpdated: string;
}

const HourlyOIDCMetrics = createDashboardFetch<TimeSeriesUserProfilesJSON>(
  "oidcActivityFetch",
  "/dashboard/hourly/users/oidc"
);

const DELETED_USERS = 'undefined';

const ProfileTypeColorMap = {
  [DELETED_USERS]: colors.grey400,
  'local,oidc': colors.green400,
  'oidc': colors.green200,
  'local': colors.blue400,
  'activeUsers': colors.yellow900,
  'inactiveUsers': colors.grey600,
};

const ProfileTypeNames = {
  [DELETED_USERS]: 'Deleted Users',
  'local,oidc': 'Local & OIDC',
  'oidc': 'OIDC',
  'local': 'Local',
  'activeUsers': 'Active Users',
  'inactiveUsers': 'Inactive Users',
};

let timeSliderTimeout: NodeJS.Timeout | null = null;

const OIDCActivity: FunctionComponent<Props> = ({
  siteID,
  lastUpdated,
}) => {

  const [timeSpanInHours, setTimeSpanInHours] = React.useState<number>(24);
  const [timeSliderValue, setTimeSliderValue] = React.useState<number>(1);
  const [withDeleted, setWithDeleted] = React.useState<boolean>(false);

  const formatter = useDateTimeFormatter({
    hour12: false,
    hour: "2-digit",
    day: "numeric",
    month: "short",
  });

  useEffect(() => {
    const hours = timeSliderValue > 0 ? ( timeSliderValue + 1 )* 24 : 24 + timeSliderValue;
    
    if (timeSliderTimeout) {
      clearTimeout(timeSliderTimeout);
    }
    
    timeSliderTimeout = setTimeout(() => setTimeSpanInHours(hours), 1000);
  }, [timeSliderValue]);
  
  const timeSpanPeriod = useMemo<{days: number, hours: number}>(() => {
    if (timeSliderValue >= 0) {
      return {
        days: timeSliderValue + 1,
        hours: 0,
      }
    } else {
      return {
        days: 0,
        hours: 24 + timeSliderValue
      }
    }
  }, [timeSliderValue, timeSpanInHours]);

  const [activity, loading] = useImmediateFetch(
    HourlyOIDCMetrics,
    { siteID, timeSpanInHours },
    lastUpdated
  );

  const seriesUserProfiles = useMemo(() => {
    if (!activity || !activity.seriesUserProfiles || activity.seriesUserProfiles.length === 0) {
      return [];
    }

    return activity
    .seriesUserProfiles
    .map((entry) => {
      const { timestamp, ...activityValues } = entry;

      if( !withDeleted ) {
        // Remove deleted profile type from the activity values to avoid showing it in the pie chart.
       delete activityValues[DELETED_USERS];
      }

      return {
        ...activityValues,
        timestamp: new Date(timestamp),
      };
    })
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  }, [activity, withDeleted]);

  const seriesActiveUsers = useMemo(() => {
    if (!activity || !activity.seriesActiveUsers || activity.seriesActiveUsers.length === 0) {
      return [];
    }

    return activity
    .seriesActiveUsers
    .map((entry) => {
      return {
        timestamp: new Date(entry.timestamp),
        count: entry.count,
      };
    })
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }, [activity]);

  const userProfilesTypes = useMemo(() => {
    const latest = seriesUserProfiles[seriesUserProfiles.length - 1];

    if (!latest) {
      return [];
    }

    const types = Object
      .keys(latest)
      .filter(key => key !== 'timestamp')

    return types;
  }, [seriesUserProfiles]);

  return (
    <DashboardBox>
      <Localized id="dashboard-oidc-activity-heading">
        <DashboardComponentHeading>
          User Report
          {loading && <Spinner size="xs"/> }
        </DashboardComponentHeading>
      </Localized>

      <Flex spacing={2} alignItems="center" justifyContent="space-between">
        <Flex direction="column" spacing={1} alignItems="flex-start">
            <label htmlFor="timeSlider">Report Time { timeSpanPeriod.days > 0 ? `${timeSpanPeriod.days} days` :  `${timeSpanPeriod.hours} hours` }</label>           
            <input
              key="timeSlider"
              type="range"
              min={-24}
              max={60}
              step={1}
              value={timeSliderValue}
              onChange={(e) => {
                setTimeSliderValue(parseInt(e.target.value, 10));
              }}
            />  
        </Flex>
        <label>
          Include deleted users
          <input
            type="checkbox"
            checked={withDeleted}
            onChange={(e) => setWithDeleted(e.target.checked)}
          />
        </label>
      </Flex>

      {!seriesUserProfiles?.length && (
        <Localized id="dashboard-oidc-activity-no-data">
          <div className={styles.heading}>No data available</div>
        </Localized>
      )}

      <OIDCActivityPie
        seriesUserProfiles={seriesUserProfiles as any}
        seriesActiveUsers={seriesActiveUsers as any} 
        userProfilesNames={ProfileTypeNames}
        userProfilesColors={ProfileTypeColorMap}
      />

      {(seriesUserProfiles.length > 0) && (
        <>
          <ResponsiveContainer height={250}>

            <LineChart data={seriesUserProfiles} syncId="usersChart">
              <XAxis 
                dataKey="timestamp"                 
                tickFormatter={(unixTime: Date) => formatter(unixTime.toISOString()).toLowerCase().replace(" ", "") }
                interval="preserveStart"
                minTickGap={20}
                tick={{ fontSize: 12, fontWeight: 600 }}
              />
              <YAxis />
              <CartesianGrid vertical={false} stroke={CHART_COLOR_GREY_200} />
              {
                userProfilesTypes.map((type) => (
                  <Line
                    key={type}
                    type="monotone"
                    dataKey={type}
                    stroke={ProfileTypeColorMap[type as keyof typeof ProfileTypeColorMap] || colors.red400}
                    legendType="line"
                  />
                ))
              }
              <Tooltip formatter={(value, name) => {
                return [value, ProfileTypeNames[name as keyof typeof ProfileTypeNames] || name];
              }}/>
            </LineChart>
          </ResponsiveContainer>
          <ResponsiveContainer height={200}>
          <LineChart data={seriesActiveUsers} key="activeUsersChart">
              <XAxis 
                dataKey="timestamp"                 
                tickFormatter={(unixTime: Date) => formatter(unixTime.toISOString()).toLowerCase().replace(" ", "") }
                interval="preserveStart"
                minTickGap={20}
                tick={{ fontSize: 12, fontWeight: 600 }}
              />
              <YAxis />
              <CartesianGrid vertical={false} stroke={CHART_COLOR_GREY_200} />               
              <Line
                key="activeUsersCount"
                type="monotone"
                dataKey="count"
                stroke={colors.green400}
                legendType="line"
              />
              <Tooltip formatter={(value) => {
                return [value, ProfileTypeNames.activeUsers];
              }}/>
          </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </DashboardBox>
  );
};

export default OIDCActivity;
